/**
 * Centralized Error Handler Utility
 * Sends email notifications when API errors occur in critical modules
 * Also logs errors to local storage and server
 */

import { sendEmail, EmailPayload } from "./sendEmail";
// import { addErrorLog } from "./errorLogger";

/**
 * Infer file name from error context (module and action)
 * This is a fallback when stack trace doesn't contain source file info
 */
function inferFileNameFromContext(module: string, action?: string): string | undefined {
  // Map common actions to likely file names
  const actionToFileMap: Record<string, string> = {
    // SignageCreateOrder.tsx
    'create_order': 'SignageCreateOrder.tsx',
    'fetch_addresses': 'SignageCreateOrder.tsx',
    'ensure_draft_unique_id': 'SignageCreateOrder.tsx',
    'load_brokerage_options': 'SignageCreateOrder.tsx',
    'load_agent_options': 'SignageCreateOrder.tsx',
    'load_office_options': 'SignageCreateOrder.tsx',
    'load_team_member_options': 'SignageCreateOrder.tsx',
    'load_office_agent_options': 'SignageCreateOrder.tsx',
    'fetch_products': 'SignageCreateOrder.tsx',
    'fetch_shipping_costs': 'SignageCreateOrder.tsx',
    'fetch_effective_price': 'SignageCreateOrder.tsx',
    // SignageEditOrder.tsx
    'update_order': 'SignageEditOrder.tsx',
    // SignageOrderDetails.tsx
    'fetch_order_data': 'SignageOrderDetails.tsx',
    'update_order_status': 'SignageOrderDetails.tsx',
    'update_order_final_status': 'SignageOrderDetails.tsx',
    'complete_order_payment': 'SignageOrderDetails.tsx',
    'fetch_payment_methods': 'SignageOrderDetails.tsx',
    'create_payment_intent': 'SignageOrderDetails.tsx',
    'fetch_parent_hierarchy': 'SignageOrderDetails.tsx',
    'fetch_customer_data': 'SignageOrderDetails.tsx',
    'fetch_payment_integrations': 'SignageOrderDetails.tsx',
    'download_invoice': 'SignageOrderDetails.tsx',
    'tax_refund_on_cancel': 'SignageOrderDetails.tsx',
    // SignageOfficeInventory.tsx
    'fetch_office_products': 'SignageOfficeInventory.tsx',
    'fetch_warehouses': 'SignageOfficeInventory.tsx',
    'unlink_office_product': 'SignageOfficeInventory.tsx',
    // SignageAgentInventory.tsx
    'fetch_agent_products': 'SignageAgentInventory.tsx',
    'unlink_agent_product': 'SignageAgentInventory.tsx',
    // SalesOrderDetails.tsx
    'taxcloud_apply_taxes_draft': 'SalesOrderDetails.tsx',
    'taxcloud_report_sales_tax_complete': 'SalesOrderDetails.tsx',
    'update_instructions': 'SalesOrderDetails.tsx',
    'finalize_taxcloud_order': 'SalesOrderDetails.tsx',
    'get_version_order_number': 'SalesOrderDetails.tsx',
    'persist_order_charges': 'SalesOrderDetails.tsx',
    // RealtorOrderCheckout.tsx
    'realtor_order_checkout_fetch_parent_hierarchy': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_fetch_order_data': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_fetch_cart_items': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_check_credit_facility': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_fetch_integrations': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_create_order': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_update_order': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_fetch_addresses': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_complete_order_payment': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_create_payment_intent': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_fetch_shipping_costs': 'RealtorOrderCheckout.tsx',
    'realtor_order_checkout_fetch_payment_methods': 'RealtorOrderCheckout.tsx',
    // AgentOrderCheckout.tsx
    'agent_order_checkout_fetch_order_data': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_fetch_addresses': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_fetch_cart_items': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_fetch_user_info': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_check_credit_facility': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_fetch_addresses_list': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_create_order': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_update_order': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_complete_order_payment': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_fetch_shipping_costs': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_create_payment_intent': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_fetch_integrations': 'AgentOrderCheckout.tsx',
    'agent_order_checkout_fetch_payment_methods': 'AgentOrderCheckout.tsx',
    // SignageAddInstallation.tsx
    'Signage_Add_Installation_fetch_parent_hierarchy': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_load_agent_options': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_load_office_options': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_fetch_addresses': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_fetch_product_by_id': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_fetch_installation_cost': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_load_brokerage_options': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_load_team_member_options': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_load_office_agent_options': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_load_signage_team_member_options': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_fetch_scheduled_jobs': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_geocode_address': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_fetch_rotational_pool_products': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_fetch_serialized_units': 'SignageAddInstallation.tsx',
    'Signage_Add_Installation_create_installation_order': 'SignageAddInstallation.tsx',
    // SignageAddRemovalRequest.tsx
    'Signage_Add_Removal_Request_fetch_parent_hierarchy': 'SignageAddRemovalRequest.tsx',
    'Signage_Add_Removal_Request_load_signage_team_member_options': 'SignageAddRemovalRequest.tsx',
    'Signage_Add_Removal_Request_load_brokerage_team_member_options': 'SignageAddRemovalRequest.tsx',
    'Signage_Add_Removal_Request_load_completed_installation_orders': 'SignageAddRemovalRequest.tsx',
    'Signage_Add_Removal_Request_fetch_removal_tracking_summary': 'SignageAddRemovalRequest.tsx',
    'Signage_Add_Removal_Request_fetch_removal_order': 'SignageAddRemovalRequest.tsx',
    'Signage_Add_Removal_Request_fetch_user_addresses': 'SignageAddRemovalRequest.tsx',
    'Signage_Add_Removal_Request_fetch_removal_cost': 'SignageAddRemovalRequest.tsx',
    'Signage_Add_Removal_Request_create_removal_order': 'SignageAddRemovalRequest.tsx',
    'Signage_Add_Removal_Request_fetch_scheduled_jobs': 'SignageAddRemovalRequest.tsx',
    'Signage_Add_Removal_Request_ensure_draft_unique_id': 'SignageAddRemovalRequest.tsx',
    // SignageInstallationWorkOrder.tsx
    'Signage_Installation_Work_Order_fetch_parent_hierarchy': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_load_agent_options': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_fetch_agent_by_id': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_fetch_order_details': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_fetch_brokerage_firms': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_load_team_member_options': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_fetch_office_agents': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_fetch_addresses': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_fetch_product_by_id': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_fetch_installation_cost': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_fetch_scheduled_jobs': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_update_gate_code': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_update_instructions': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_geocode_address': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_update_production_status': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_fetch_installation_locations': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_get_installation_location_by_id': 'SignageInstallationWorkOrder.tsx',
    'Signage_Installation_Work_Order_handle_installation_status': 'SignageInstallationWorkOrder.tsx',
    // SignageRemovalWorkOrder.tsx
    'Signage_Removal_Work_Order_fetch_parent_hierarchy': 'SignageRemovalWorkOrder.tsx',
    'Signage_Removal_Work_Order_fetch_removal_cost': 'SignageRemovalWorkOrder.tsx',
    'Signage_Removal_Work_Order_fetch_removal_order': 'SignageRemovalWorkOrder.tsx',
    'Signage_Removal_Work_Order_fetch_warehouses': 'SignageRemovalWorkOrder.tsx',
    'Signage_Removal_Work_Order_handle_removal_status': 'SignageRemovalWorkOrder.tsx',
    // SpecialFeeTaxCloud.tsx
    'SpecialFeeTaxCloud_fetch_special_fees': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_fetch_groups': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_is_user_in_group_via_api': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_fetch_default_warehouse_address': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_upsert_tax_cloud_cart': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_ping_tax_cloud': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_create_tax_cloud_order': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_complete_tax_cloud_order': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_refund': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_post_version_order_number': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_get_version_order_number': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_refund_tax_cloud_order': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_fetch_ups_integrations': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_fetch_ups_shipper_addresses': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_fetch_ups_rates': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_taxcloud_upsert_cart_v2': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_taxcloud_create_order_v2': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_complete_tax_cloud_order_v2': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_fetch_userbase_special_fee': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_ping_tax_cloud_installation_and_removals': 'specialFeeTaxCloud.tsx',
    'SpecialFeeTaxCloud_taxcloud_ping_v2': 'specialFeeTaxCloud.tsx',
    // InstallationandRemovalTaxcloud.tsx
    'InstallationandRemovalTaxcloud_fetch_default_warehouse_address': 'InstallationandRemovalTaxcloud.tsx',
    'InstallationandRemovalTaxcloud_ping_tax_cloud': 'InstallationandRemovalTaxcloud.tsx',
    'InstallationandRemovalTaxcloud_upsert_tax_cloud_cart': 'InstallationandRemovalTaxcloud.tsx',
    'InstallationandRemovalTaxcloud_create_tax_cloud_order': 'InstallationandRemovalTaxcloud.tsx',
    'InstallationandRemovalTaxcloud_complete_tax_cloud_order': 'InstallationandRemovalTaxcloud.tsx',
    'InstallationandRemovalTaxcloud_refund': 'InstallationandRemovalTaxcloud.tsx',
    'InstallationandRemovalTaxcloud_post_version_order_number': 'InstallationandRemovalTaxcloud.tsx',
    'InstallationandRemovalTaxcloud_get_version_order_number': 'InstallationandRemovalTaxcloud.tsx',
    'InstallationandRemovalTaxcloud_refund_tax_cloud_order': 'InstallationandRemovalTaxcloud.tsx',
    // RealtorandAgentCouponDiscount.tsx
    'RealtorandAgentCouponDiscount_resolve_and_apply': 'RealtorandAgentCouponDiscount.tsx',
    // CouponExpirationCheck.tsx
    'CouponExpirationCheck_validate_coupon': 'CouponExpirationCheck.tsx',
    'CouponExpirationCheck_check_and_proceed': 'CouponExpirationCheck.tsx',
    // Volumediscountcalls.tsx
    'Volumediscountcalls_create_volume_discount': 'Volumediscountcalls.tsx',
    'Volumediscountcalls_update_volume_discount': 'Volumediscountcalls.tsx',
    'Volumediscountcalls_delete_volume_discount': 'Volumediscountcalls.tsx',
    'Volumediscountcalls_get_variant_discounts_datatable': 'Volumediscountcalls.tsx',
    'Volumediscountcalls_get_discounts_by_variant': 'Volumediscountcalls.tsx',
    'Volumediscountcalls_get_discount_by_id': 'Volumediscountcalls.tsx',
    // CouponDiscount.tsx
    'CouponDiscount_resolve_and_apply': 'CouponDiscount.tsx',
    // ShippingOptionsui.tsx
    'ShippingOptionsui_get_active_tax_type': 'ShippingOptionsui.tsx',
    'ShippingOptionsui_load_manual': 'ShippingOptionsui.tsx',
    'ShippingOptionsui_load_ups_configs': 'ShippingOptionsui.tsx',
    'ShippingOptionsui_load_shippers': 'ShippingOptionsui.tsx',
    'ShippingOptionsui_load_ups_rates': 'ShippingOptionsui.tsx',
    'ShippingOptionsui_calc_shipping_by_type_manual': 'ShippingOptionsui.tsx',
    'ShippingOptionsui_calc_shipping_by_type_ups': 'ShippingOptionsui.tsx',
    // InstallationandremovalsmanualTax.tsx
    'InstallationandremovalsmanualTax_get_active_tax_type': 'InstallationandremovalsmanualTax.tsx',
    'InstallationandremovalsmanualTax_post_installation_manual_tax': 'InstallationandremovalsmanualTax.tsx',
    'InstallationandremovalsmanualTax_post_special_fee_manual_tax': 'InstallationandremovalsmanualTax.tsx',
    // InstallationandremovalCouponDiscount.tsx
    'InstallationandremovalCouponDiscount_resolve_job': 'InstallationandremovalCouponDiscount.tsx',
    // InstallationandremovalSpecialfeeui.tsx
    'InstallationandremovalSpecialfeeui_fetch_special_fees': 'InstallationandremovalSpecialfeeui.tsx',
    // manualtax.tsx
    'manualtax_get_active_tax_type': 'manualtax.tsx',
    'manualtax_ensure_ups_integration_id': 'manualtax.tsx',
    'manualtax_calc_manual_shipping_tax': 'manualtax.tsx',
    'manualtax_calc_ups_shipping_tax': 'manualtax.tsx',
    'manualtax_calc_special_fee_tax': 'manualtax.tsx',
    'manualtax_calc_products_tax_manual': 'manualtax.tsx',
    'manualtax_post_installation_manual_tax': 'manualtax.tsx',
    'manualtax_post_special_fee_manual_tax': 'manualtax.tsx',
    // RealtorAddInstallation.tsx
    'Realtor_Add_Installation_fetch_license': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_fetch_parent_hierarchy': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_load_office_options': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_load_team_member_options': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_load_office_agent_options': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_load_agent_options': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_fetch_suppliers_by_user_id': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_fetch_addresses': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_fetch_linked_products': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_fetch_product_by_id': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_fetch_installation_cost': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_create_installation_order': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_fetch_order_for_email': 'RealtorAddInstallation.tsx',
    'Realtor_Add_Installation_ensure_draft_unique_id': 'RealtorAddInstallation.tsx',
    // RealtorViewInstallation.tsx
    'Realtor_View_Installation_fetch_parent_hierarchy': 'RealtorViewInstallation.tsx',
    'Realtor_View_Installation_fetch_order_details': 'RealtorViewInstallation.tsx',
    // RealtorAddRemovalRequest.tsx
    'Realtor_Add_Removal_Request_load_completed_installation_order_options': 'RealtorAddRemovalRequest.tsx',
    'Realtor_Add_Removal_Request_fetch_removal_tracking_summary': 'RealtorAddRemovalRequest.tsx',
    'Realtor_Add_Removal_Request_fetch_removal_order': 'RealtorAddRemovalRequest.tsx',
    'Realtor_Add_Removal_Request_fetch_addresses': 'RealtorAddRemovalRequest.tsx',
    'Realtor_Add_Removal_Request_fetch_removal_cost': 'RealtorAddRemovalRequest.tsx',
    'Realtor_Add_Removal_Request_fetch_parent_hierarchy': 'RealtorAddRemovalRequest.tsx',
    'Realtor_Add_Removal_Request_load_team_member_options': 'RealtorAddRemovalRequest.tsx',
    'Realtor_Add_Removal_Request_ensure_draft_unique_id': 'RealtorAddRemovalRequest.tsx',
    'Realtor_Add_Removal_Request_create_removal_order': 'RealtorAddRemovalRequest.tsx',
    'Realtor_Add_Removal_Request_upload_file': 'RealtorAddRemovalRequest.tsx',
    // RealtorEditRemovalRequest.tsx
    'Realtor_Edit_Removal_Request_fetch_final_submitted_items': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_fetch_parent_hierarchy': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_fetch_removal_cost_dynamic': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_fetch_all_dropdowns': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_fetch_removal_order': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_fetch_addresses': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_fetch_integrations': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_fetch_payment_intent': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_handle_removal_status': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_complete_order': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_get_version_order_number': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_request_installation_tax_refund': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_upload_file': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_handle_save_gate_code': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_handle_save_instructions_only': 'RealtorEditRemovalRequest.tsx',
    'Realtor_Edit_Removal_Request_fetch_payment_methods': 'RealtorEditRemovalRequest.tsx',
    // AgentAddInstallation.tsx
    'Agent_Add_Installation_fetch_parent_hierarchy': 'AgentAddInstallation.tsx',
    'Agent_Add_Installation_fetch_license': 'AgentAddInstallation.tsx',
    'Agent_Add_Installation_fetch_suppliers_by_user_id': 'AgentAddInstallation.tsx',
    'Agent_Add_Installation_fetch_addresses': 'AgentAddInstallation.tsx',
    'Agent_Add_Installation_fetch_addresses_refresh': 'AgentAddInstallation.tsx',
    'Agent_Add_Installation_fetch_installation_cost': 'AgentAddInstallation.tsx',
    'Agent_Add_Installation_fetch_order_for_email': 'AgentAddInstallation.tsx',
    'Agent_Add_Installation_create_installation_order': 'AgentAddInstallation.tsx',
    'Agent_Add_Installation_fetch_linked_products': 'AgentAddInstallation.tsx',
    'Agent_Add_Installation_fetch_product_by_id': 'AgentAddInstallation.tsx',
    'Agent_Add_Installation_ensure_draft_unique_id': 'AgentAddInstallation.tsx',
    // AgentViewInstallation.tsx
    'Agent_View_Installation_fetch_parent_hierarchy': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_license': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_order_details': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_suppliers_by_user_id': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_addresses': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_linked_products': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_product_by_id': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_installation_cost': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_order_for_edit_email': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_update_installation_order': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_payment_methods': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_integrations': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_order_for_status_email': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_handle_installation_status': 'AgentViewInstallation.tsx',
    'Agent_View_Installation_fetch_payment_intent': 'AgentViewInstallation.tsx',
    // AgentViewRemovalRequest.tsx
    'Agent_View_Removal_Request_fetch_parent_hierarchy': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_final_submitted_items': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_all_dropdowns': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_addresses': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_removal_order': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_removal_cost_dynamic': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_integrations': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_order_for_edit_email': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_removal_order_refresh': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_auto_finalize_tax': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_handle_removal_status': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_order_for_status_email': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_complete_order': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_payment_intent': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_handle_save_gate_code': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_handle_save_instructions_only': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_payment_methods': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_fetch_addresses_refresh': 'AgentViewRemovalRequest.tsx',
    'Agent_View_Removal_Request_upload_file': 'AgentViewRemovalRequest.tsx',
    // AgentAddRemovalRequest.tsx
    'Agent_Add_Removal_Request_fetch_parent_hierarchy': 'AgentAddRemovalRequest.tsx',
    'Agent_Add_Removal_Request_load_completed_installation_order_options': 'AgentAddRemovalRequest.tsx',
    'Agent_Add_Removal_Request_fetch_removal_tracking_summary': 'AgentAddRemovalRequest.tsx',
    'Agent_Add_Removal_Request_fetch_removal_order': 'AgentAddRemovalRequest.tsx',
    'Agent_Add_Removal_Request_fetch_addresses': 'AgentAddRemovalRequest.tsx',
    'Agent_Add_Removal_Request_fetch_removal_cost': 'AgentAddRemovalRequest.tsx',
    'Agent_Add_Removal_Request_fetch_order_for_email': 'AgentAddRemovalRequest.tsx',
    'Agent_Add_Removal_Request_create_removal_order': 'AgentAddRemovalRequest.tsx',
    'Agent_Add_Removal_Request_ensure_draft_unique_id': 'AgentAddRemovalRequest.tsx',
    'Agent_Add_Removal_Request_fetch_addresses_refresh': 'AgentAddRemovalRequest.tsx',
    // SignageAddVariableProduct.tsx
    'Signage_Add_Variable_Product_fetch_hierarchy': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_fetch_warehouses': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_fetch_attributes': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_fetch_tax_groups': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_fetch_tax_rules': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_create_variant': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_update_variant': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_update_product': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_delete_variant': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_fetch_variants': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_fetch_first_variant_id': 'SignageAddVariableProduct.tsx',
    'Signage_Add_Variable_Product_save_draft': 'SignageAddVariableProduct.tsx',
    // SignageEditVariableProduct.tsx
    'Signage_Edit_Variable_Product_fetch_hierarchy': 'SignageEditVariableProduct.tsx',
    'Signage_Edit_Variable_Product_fetch_product': 'SignageEditVariableProduct.tsx',
    'Signage_Edit_Variable_Product_fetch_warehouses': 'SignageEditVariableProduct.tsx',
    'Signage_Edit_Variable_Product_fetch_attributes': 'SignageEditVariableProduct.tsx',
    'Signage_Edit_Variable_Product_fetch_tax_groups': 'SignageEditVariableProduct.tsx',
    'Signage_Edit_Variable_Product_fetch_tax_rules': 'SignageEditVariableProduct.tsx',
    'Signage_Edit_Variable_Product_create_variant': 'SignageEditVariableProduct.tsx',
    'Signage_Edit_Variable_Product_update_variant': 'SignageEditVariableProduct.tsx',
    'Signage_Edit_Variable_Product_delete_variant': 'SignageEditVariableProduct.tsx',
    'Signage_Edit_Variable_Product_update_product': 'SignageEditVariableProduct.tsx',
    // Imageshelper.tsx
    'Imageshelper_upload_cloud_file': 'Imageshelper.tsx',
    'Imageshelper_get_cloud_file_by_id': 'Imageshelper.tsx',
    'Imageshelper_upload_form_data_and_get_id': 'Imageshelper.tsx',
    'Imageshelper_post_json': 'Imageshelper.tsx',
    'Imageshelper_hard_delete_presigned_files': 'Imageshelper.tsx',
    'Imageshelper_hard_delete_cloud_file': 'Imageshelper.tsx',
    // Signagewarehouse.tsx
    'Signagewarehouse_fetch_warehouse': 'Signagewarehouse.tsx',
    'Signagewarehouse_create_warehouse': 'Signagewarehouse.tsx',
    'Signagewarehouse_update_warehouse': 'Signagewarehouse.tsx',
  };
  
  if (action && actionToFileMap[action]) {
    return actionToFileMap[action];
  }
  
  // Fallback: infer from module
  const moduleToFileMap: Record<string, string> = {
    'sales_order': 'SignageCreateOrder.tsx', // Default for sales_order
    'inventory': 'SignageOfficeInventory.tsx', // Default for inventory
    'installation': 'SignageAddInstallation.tsx', // Default for installation
    'removal': 'SignageAddRemovalRequest.tsx', // Default for removal
    'other': 'specialFeeTaxCloud.tsx', // Default for other (tax, discount, etc.)
  };
  
  return moduleToFileMap[module];
}

/**
 * Extract file name and line number from stack trace
 * Creates a fresh stack trace to capture the actual call site
 * Prioritizes source files over bundle.js
 */
function extractFileAndLine(originalStack?: string): { fileName?: string; lineNumber?: number } {
  if (!originalStack) return {};
  
  const stackLines = originalStack.split('\n');
  const sourceFiles: Array<{ fileName: string; lineNumber: number; priority: number }> = [];
  
  // Look through stack trace lines - we need to find the actual source file
  for (let i = 0; i < stackLines.length && i < 30; i++) {
    const line = stackLines[i];
    
    // Skip lines from errorHandler.ts itself and internal error handling
    if (line.includes('errorHandler') || 
        line.includes('ErrorHandler') ||
        line.includes('handleApiError') ||
        line.includes('handleErrorWithEmail') ||
        line.includes('extractFileAndLine')) {
      continue;
    }
    
    // Pattern 1: Match webpack:// source map references (highest priority)
    // "webpack:///./src/pages/signage/SignageCreateOrder.tsx?1742:45"
    // "webpack:///./src/pages/signage/SignageCreateOrder.tsx:1742:45"
    // "webpack:///./src/components/SalesOrderDetails.tsx:2450:45"
    const webpackPattern = /webpack:\/\/\/\.\/src\/(?:pages|components)\/([A-Za-z][A-Za-z0-9_\-/]*\.(?:tsx?|jsx?))(?::(\d+))(?::\d+)?/;
    const webpackMatch = line.match(webpackPattern);
    if (webpackMatch) {
      const fullPath = webpackMatch[1];
      const fileName = fullPath.split('/').pop() || fullPath;
      const lineNumber = parseInt(webpackMatch[2], 10);
      if (fileName && !fileName.includes('errorHandler')) {
        sourceFiles.push({ fileName, lineNumber, priority: 0 }); // Highest priority
        continue;
      }
    }
    
    // Pattern 2: Match source files in URLs (development mode with source maps)
    // - "at onSubmit (http://localhost:3000/src/pages/signage/SignageCreateOrder.tsx:1742:45)"
    // - "at (http://localhost:3000/src/pages/signage/SignageCreateOrder.tsx:1742:45)"
    // - "at http://localhost:3000/src/pages/signage/SignageCreateOrder.tsx:1742:45"
    const urlSourcePattern = /(?:\(|at\s+)?(?:https?:\/\/[^/]+)?\/src\/(?:pages|components)\/([A-Za-z][A-Za-z0-9_\-/]*\.(?:tsx?|jsx?))(?::(\d+))(?::\d+)?/;
    const urlMatch = line.match(urlSourcePattern);
    if (urlMatch) {
      const fullPath = urlMatch[1];
      const fileName = fullPath.split('/').pop() || fullPath;
      const lineNumber = parseInt(urlMatch[2], 10);
      if (fileName && !fileName.includes('errorHandler')) {
        sourceFiles.push({ fileName, lineNumber, priority: 1 });
        continue;
      }
    }
    
    // Pattern 3: Match direct source file references
    // - "at SignageCreateOrder.tsx:1742:45"
    // - "at (SignageCreateOrder.tsx:1742:45)"
    // - "SignageCreateOrder.tsx:1742"
    const directSourcePattern = /(?:\(|at\s+)?(?:[^\s]*\/)?([A-Z][A-Za-z0-9_\-]+\.(?:tsx?|jsx?))(?::(\d+))(?::\d+)?/;
    const directMatch = line.match(directSourcePattern);
    if (directMatch) {
      const fileName = directMatch[1];
      const lineNumber = parseInt(directMatch[2], 10);
      
      // Skip bundle files, errorHandler, and common library files
      if (!fileName.includes('errorHandler') && 
          !fileName.includes('bundle') &&
          !fileName.includes('chunk') &&
          !fileName.includes('vendor') &&
          !fileName.includes('node_modules')) {
        const priority = fileName.endsWith('.tsx') || fileName.endsWith('.ts') ? 2 : 3;
        sourceFiles.push({ fileName, lineNumber, priority });
        continue;
      }
    }
    
    // Pattern 4: When we see bundle.js, look for source map info in the same or next lines
    // "at onSubmit (http://localhost:3000/static/js/bundle.js:723507:17)"
    // Sometimes source maps appear in comments or next lines
    if (line.includes('bundle.js') || line.includes('chunk.js')) {
      // Look ahead in the stack for source map references (up to 10 lines ahead)
      for (let j = i + 1; j < stackLines.length && j < i + 10; j++) {
        const nextLine = stackLines[j];
        
        // Check for webpack source map in next line
        const nextWebpackMatch = nextLine.match(/webpack:\/\/\/\.\/src\/(?:pages|components)\/([A-Za-z][A-Za-z0-9_\-/]*\.(?:tsx?|jsx?))(?::(\d+))/);
        if (nextWebpackMatch) {
          const fullPath = nextWebpackMatch[1];
          const fileName = fullPath.split('/').pop() || fullPath;
          const lineNumber = parseInt(nextWebpackMatch[2], 10);
          if (fileName && !fileName.includes('errorHandler')) {
            sourceFiles.push({ fileName, lineNumber, priority: 0 });
            break;
          }
        }
        
        // Also check for URL source patterns in next lines
        const nextUrlMatch = nextLine.match(/\/src\/(?:pages|components)\/([A-Za-z][A-Za-z0-9_\-/]*\.(?:tsx?|jsx?))(?::(\d+))/);
        if (nextUrlMatch) {
          const fullPath = nextUrlMatch[1];
          const fileName = fullPath.split('/').pop() || fullPath;
          const lineNumber = parseInt(nextUrlMatch[2], 10);
          if (fileName && !fileName.includes('errorHandler') && !fileName.includes('bundle')) {
            sourceFiles.push({ fileName, lineNumber, priority: 1 });
            break;
          }
        }
      }
    }
  }
  
  // If we found source files, return the one with highest priority (lowest number)
  if (sourceFiles.length > 0) {
    // Sort by priority (0 = highest, then 1, 2, 3)
    sourceFiles.sort((a, b) => a.priority - b.priority);
    const bestMatch = sourceFiles[0];
    return { fileName: bestMatch.fileName, lineNumber: bestMatch.lineNumber };
  }
  
  // Last resort: Search entire stack for any source file patterns (case-insensitive)
  // This is a broader search that looks for any .tsx/.ts files mentioned anywhere
  const allSourceMatches = originalStack.match(/([A-Z][A-Za-z0-9_\-]+\.(?:tsx?|ts))/g);
  if (allSourceMatches && allSourceMatches.length > 0) {
    // Filter out common library files and errorHandler
    const validFiles = Array.from(new Set(allSourceMatches)).filter(f => {
      const lower = f.toLowerCase();
      return !lower.includes('errorhandler') && 
             !lower.includes('node_modules') &&
             !lower.includes('bundle') &&
             !lower.includes('chunk') &&
             !lower.includes('vendor') &&
             !lower.includes('webpack');
    });
    
    if (validFiles.length > 0) {
      // Try to find line number associated with the first valid file
      const fileRef = validFiles[0];
      // Look for pattern: fileName:lineNumber (case-insensitive)
      const filePattern = new RegExp(`${fileRef.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:(\\d+)`, 'i');
      const lineMatch = originalStack.match(filePattern);
      
      return {
        fileName: fileRef,
        lineNumber: lineMatch ? parseInt(lineMatch[1], 10) : undefined
      };
    }
  }
  
  return {};
}

export interface ErrorContext {
  module: "inventory" | "sales_order" | "installation" | "removal" | "other";
  action?: string; // e.g., "fetch_products", "create_order", "update_status"
  userId?: string;
  EnduserId?: string; // For actions performed on behalf of another user
  userEmail?: string;
  licenseType?: string; // User's license type from localStorage
  userName?: string; // User's name from localStorage
  additionalInfo?: Record<string, any>;
  url?: string;
  method?: string;
  requestBody?: any;
  fileName?: string; // File name where error occurred (e.g., "SignageCreateOrder.tsx")
  lineNumber?: number; // Line number where error occurred
}

/**
 * Format error details for email
 */
function formatErrorDetails(error: any, context: ErrorContext): string {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;
  
  const details = [
    `<h3 style="color: #dc3545; margin-top: 20px;">Error Details</h3>`,
    `<table style="width: 100%; border-collapse: collapse; margin-top: 10px;">`,
    `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold; width: 200px;">Module</td><td style="padding: 8px; border: 1px solid #ddd;">${context.module}</td></tr>`,
    context.action ? `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">Action</td><td style="padding: 8px; border: 1px solid #ddd;">${context.action}</td></tr>` : '',
    context.fileName ? `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">File Name</td><td style="padding: 8px; border: 1px solid #ddd; font-family: monospace;">${escapeHtml(context.fileName)}</td></tr>` : '',
    context.lineNumber ? `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">Line Number</td><td style="padding: 8px; border: 1px solid #ddd;">${context.lineNumber}</td></tr>` : '',
    `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">Error Message</td><td style="padding: 8px; border: 1px solid #ddd; color: #dc3545;">${escapeHtml(errorMessage)}</td></tr>`,
    context.url ? `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">API URL</td><td style="padding: 8px; border: 1px solid #ddd; font-family: monospace; font-size: 12px;">${escapeHtml(context.url)}</td></tr>` : '',
    context.method ? `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">HTTP Method</td><td style="padding: 8px; border: 1px solid #ddd;">${context.method}</td></tr>` : '',
    context.userId ? `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">User ID</td><td style="padding: 8px; border: 1px solid #ddd;">${context.userId}</td></tr>` : '',
    context.userEmail ? `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">User Email</td><td style="padding: 8px; border: 1px solid #ddd;">${context.userEmail}</td></tr>` : '',
    context.licenseType ? `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">License Type</td><td style="padding: 8px; border: 1px solid #ddd;">${escapeHtml(context.licenseType)}</td></tr>` : '',
    context.userName ? `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">User Name</td><td style="padding: 8px; border: 1px solid #ddd;">${escapeHtml(context.userName)}</td></tr>` : '',
    `<tr><td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; font-weight: bold;">Timestamp</td><td style="padding: 8px; border: 1px solid #ddd;">${new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })}</td></tr>`,
    `</table>`,
  ];

  if (context.requestBody) {
    details.push(
      `<h4 style="color: #6c757d; margin-top: 20px;">Request Body</h4>`,
      `<pre style="background: #064e3b; color: #d1fae5; padding: 15px; border-radius: 4px; overflow-x: auto; overflow-y: auto; font-size: 11px; max-height: 400px; font-family: monospace; line-height: 1.6;">${escapeHtml(JSON.stringify(context.requestBody, null, 2))}</pre>`
    );
  }

  if (errorStack) {
    details.push(
      `<h4 style="color: #6c757d; margin-top: 20px;">Stack Trace</h4>`,
      `<pre style="background: #f8f9fa; padding: 15px; border-radius: 4px; overflow-x: auto; font-size: 11px; max-height: 300px; overflow-y: auto;">${escapeHtml(errorStack)}</pre>`
    );
  }

  if (context.additionalInfo && Object.keys(context.additionalInfo).length > 0) {
    details.push(
      `<h4 style="color: #6c757d; margin-top: 20px;">Additional Information</h4>`,
      `<pre style="background: #f8f9fa; padding: 15px; border-radius: 4px; overflow-x: auto; font-size: 11px;">${escapeHtml(JSON.stringify(context.additionalInfo, null, 2))}</pre>`
    );
  }
  const clientInfo = getClientInfo();
  if (clientInfo) {
    details.push(
      `<h4 style="color: #6c757d; margin-top: 20px;">Client Info</h4>`,
      `<pre style="background: #f8f9fa; padding: 15px; border-radius: 4px; overflow-x: auto; font-size: 11px; max-height: 300px; overflow-y: auto;">${escapeHtml(
        JSON.stringify(clientInfo, null, 2)
      )}</pre>`
    );
  }

  return details.filter(Boolean).join('');
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  if (typeof window === 'undefined') {
    // Server-side or non-browser environment
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Generate email body format for UI display (React-friendly)
 */
export function generateEmailBodyForUI(
  error: { message?: string; stack?: string } | string,
  context: ErrorContext,
  timestamp?: string
): {
  moduleName: string;
  errorDetails: Array<{ label: string; value: string }>;
  stackTrace?: string;
  additionalInfo?: string;
  requestBody?: string;
} {
  const moduleNames: Record<string, string> = {
    inventory: "Inventory",
    sales_order: "Sales Order",
    installation: "Installation",
    removal: "Removal",
    other: "General"
  };

  const moduleName = moduleNames[context.module] || "Unknown";
  
  // Handle error message - can be Error object, object with message property, or string
  let errorMessage: string;
  let errorStack: string | undefined;
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorStack = error.stack;
  } else if (error && typeof error === 'object' && 'message' in error) {
    errorMessage = String(error.message);
    errorStack = error.stack;
  } else {
    errorMessage = String(error);
  }

  // Use provided timestamp or current time
  const displayTimestamp = timestamp 
    ? new Date(timestamp).toLocaleString('en-US', { timeZone: 'America/New_York' })
    : new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });

  const errorDetails: Array<{ label: string; value: string }> = [
    { label: "Module", value: context.module },
    ...(context.action ? [{ label: "Action", value: context.action }] : []),
    ...(context.fileName ? [{ label: "File Name", value: context.fileName }] : []),
    ...(context.lineNumber ? [{ label: "Line Number", value: String(context.lineNumber) }] : []),
    { label: "Error Message", value: errorMessage },
    ...(context.url ? [{ label: "API URL", value: context.url }] : []),
    ...(context.method ? [{ label: "HTTP Method", value: context.method }] : []),
    ...(context.userId ? [{ label: "User ID", value: context.userId }] : []),
      ...(context.EnduserId ? [{ label: "End User ID", value: context.EnduserId }] : []),
    ...(context.userEmail ? [{ label: "User Email", value: context.userEmail }] : []),
    ...(context.licenseType ? [{ label: "License Type", value: context.licenseType }] : []),
    ...(context.userName ? [{ label: "User Name", value: context.userName }] : []),
    { label: "Timestamp", value: displayTimestamp },
  ];

  return {
    moduleName,
    errorDetails,
    stackTrace: errorStack,
    additionalInfo: context.additionalInfo ? JSON.stringify(context.additionalInfo, null, 2) : undefined,
    requestBody: context.requestBody ? JSON.stringify(context.requestBody, null, 2) : undefined,
  };
}

/**
 * Generate HTML email body
 */
function generateErrorEmailBody(error: any, context: ErrorContext): string {
  const moduleNames: Record<string, string> = {
    inventory: "Inventory",
    sales_order: "Sales Order",
    installation: "Installation",
    removal: "Removal",
    other: "General"
  };

  const moduleName = moduleNames[context.module] || "Unknown";

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 800px; margin: 0 auto; padding: 20px; }
        .header { background: #dc3545; color: white; padding: 20px; border-radius: 5px 5px 0 0; }
        .content { background: #fff; padding: 20px; border: 1px solid #ddd; border-top: none; }
        .footer { background: #f8f9fa; padding: 15px; text-align: center; font-size: 12px; color: #6c757d; border: 1px solid #ddd; border-top: none; border-radius: 0 0 5px 5px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h2 style="margin: 0;">⚠️ API Error Alert</h2>
          <p style="margin: 10px 0 0 0; font-size: 14px;">An error occurred in ${moduleName} module</p>
        </div>
        <div class="content">
          ${formatErrorDetails(error, context)}
        </div>
        <div class="footer">
          <p style="margin: 0;">This is an automated error notification from RealEsy Application</p>
          <p style="margin: 5px 0 0 0;">Please investigate and resolve the issue promptly.</p>
        </div>
      </div>
    </body>
    </html>
  `;
}

const ERROR_EMAIL_DEDUPE_MS = 60_000;
const recentErrorEmails = new Map<string, number>();

function errorEmailDedupeKey(error: unknown, context: ErrorContext): string {
  const msg = error instanceof Error ? error.message : String(error);
  return `${context.module}|${context.action || ""}|${msg.slice(0, 200)}`;
}

function shouldSkipDuplicateErrorEmail(error: unknown, context: ErrorContext): boolean {
  const key = errorEmailDedupeKey(error, context);
  const now = Date.now();
  const lastSent = recentErrorEmails.get(key);
  if (lastSent != null && now - lastSent < ERROR_EMAIL_DEDUPE_MS) {
    return true;
  }
  recentErrorEmails.set(key, now);
  return false;
}

/** Missing/stale work-drive file ids are expected during image presign — never email for these. */
function shouldSuppressWorkDriveFileLookupEmail(error: unknown, context: ErrorContext): boolean {
  const action = String(context.action || "");
  if (action === "Imageshelper_get_cloud_file_by_id") return true;

  const url = String(context.url || "").toLowerCase();
  const method = String(context.method || "GET").toUpperCase();
  const isSingleFileGet =
    method === "GET" &&
    /\/work-drive\/files\/[0-9a-f]{24}(?:\/|$)/i.test(url);
  if (!isSingleFileGet) return false;

  const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return (
    msg.includes("not found") ||
    msg.includes("404") ||
    msg.includes("fetch file failed")
  );
}

/**
 * Get developer emails from environment variables
 * Returns an array of email addresses to send error notifications to
 * You can set REACT_APP_DEVELOPER_EMAIL1 and REACT_APP_DEVELOPER_EMAIL2 in your .env file
 */
function getAdminEmail(): string[] {
  const emails: string[] = [];
  const email1 = process.env.REACT_APP_DEVELOPER_EMAIL1;
  const email2 = process.env.REACT_APP_DEVELOPER_EMAIL2;
  
  console.log(`[Error Handler] getAdminEmail - email1: ${email1}, email2: ${email2}`);
  
  if (email1 && email1.trim()) {
    emails.push(email1.trim());
  }
  if (email2 && email2.trim()) {
    emails.push(email2.trim());
  }
  
  console.log(`[Error Handler] getAdminEmail - returning emails:`, emails);
  return emails;
}

/**
 * Get from email from environment variable
 */
function getFromEmail(): string {
  return process.env.REACT_APP_FROM_EMAIL2 || "";
}

/**
 * Main function to handle errors and send email notifications
 * 
 * @param error - The error object
 * @param context - Context information about where the error occurred
 * @param token - JWT token for API authentication
 * @param options - Optional configuration
 */
export async function handleErrorWithEmail(
  error: any,
  context: ErrorContext,
  token?: string | null,
  options: {
    sendEmail?: boolean;
    logToConsole?: boolean;
    throwError?: boolean;
  } = {}
): Promise<void> {
  const {
    sendEmail: shouldSendEmail = true,
    logToConsole = true,
    throwError: shouldThrow = false
  } = options;

  // Log to console
  if (logToConsole) {
    console.error(`[Error Handler] ${context.module} - ${context.action || 'Unknown action'}:`, error);
    console.error('[Error Context]:', context);
  }

  // Store error in log file (localStorage + server)
  try {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    
    // Automatically extract file name and line number from stack trace
    // Create a fresh stack trace to capture the actual call site
    let fileName = context.fileName;
    let lineNumber = context.lineNumber;
    
    // Extract file name and line number from the error's stack trace
    // The stack trace should contain source map information in development mode
    const extracted = extractFileAndLine(errorStack);
    
    // Always prefer extracted source files (not bundle.js) over manually provided values
    // This ensures we get the actual source file location
    if (extracted.fileName && 
        !extracted.fileName.includes('bundle') && 
        !extracted.fileName.includes('chunk') &&
        !extracted.fileName.includes('vendor')) {
      fileName = extracted.fileName;
      if (extracted.lineNumber) {
        lineNumber = extracted.lineNumber;
      }
    } else if (!fileName && extracted.fileName) {
      // If no manual override and we got something, use it
      fileName = extracted.fileName;
      if (extracted.lineNumber) {
        lineNumber = extracted.lineNumber;
      }
    }
    
    // If we still don't have a source file (or only have bundle.js), try to infer from context
    // This is a fallback when source maps aren't available in the stack trace
    if (!fileName || fileName.includes('bundle') || fileName.includes('chunk')) {
      const inferredFileName = inferFileNameFromContext(context.module, context.action);
      if (inferredFileName) {
        fileName = inferredFileName;
        // We can't infer line number from context, so leave it undefined
        // The line number from bundle.js is not useful, so we don't use it
        lineNumber = undefined; // Clear bundle.js line number
      }
    }
    
    // Debug logging (only in development)
    if (process.env.NODE_ENV === 'development' && (!fileName || fileName.includes('bundle'))) {
      console.warn('[Error Handler] Could not extract source file from stack trace:', {
        extracted: extracted,
        inferred: inferFileNameFromContext(context.module, context.action),
        stackPreview: errorStack?.split('\n').slice(0, 5).join('\n')
      });
    }

    // addErrorLog({
    //   module: context.module,
    //   action: context.action || "unknown",
    //   error: {
    //     message: errorMessage,
    //     stack: errorStack,
    //   },
    //   context: {
    //     userId: context.userId,
    //     userEmail: context.userEmail,
    //     licenseType: context.licenseType,
    //     userName: context.userName,
    //     url: context.url,
    //     method: context.method,
    //     requestBody: context.requestBody,
    //     additionalInfo: context.additionalInfo,
    //     fileName: fileName,
    //     lineNumber: lineNumber,
    //   },
    // });
  } catch (logError) {
    console.error("[Error Handler] Failed to log error to file:", logError);
    // Don't throw - logging failure shouldn't break error handling
  }

  // Send email notification
  console.log(`[Error Handler] Email sending check - shouldSendEmail: ${shouldSendEmail}, hasToken: ${!!token}`);

  if (shouldSendEmail && shouldSuppressWorkDriveFileLookupEmail(error, context)) {
    console.warn("[Error Handler] Skipping email for work-drive file lookup failure (missing file id)");
    return;
  }
  
  if (shouldSendEmail && token) {
    try {
      const adminEmails = getAdminEmail();
      console.log(`[Error Handler] Retrieved admin emails:`, adminEmails);
      console.log(`[Error Handler] Retrieved admin emails (joined): ${adminEmails.join(', ')}`);
      
      if (adminEmails.length === 0) {
        console.warn('[Error Handler] No recipient emails configured. Please set REACT_APP_DEVELOPER_EMAIL1 or REACT_APP_DEVELOPER_EMAIL2');
        return;
      }
      
      if (shouldSkipDuplicateErrorEmail(error, context)) {
        console.warn("[Error Handler] Skipping duplicate error email within dedupe window");
        return;
      }

      console.log(`[Error Handler] Preparing to send error notification email to ${adminEmails.join(', ')}`);
      const fromEmail = getFromEmail();
      
      if (!fromEmail || !fromEmail.trim()) {
        console.warn('[Error Handler] No from email configured. Please set REACT_APP_FROM_EMAIL2');
        return;
      }
      
      const emailBody = generateErrorEmailBody(error, context);
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      const emailPayload: EmailPayload = {
        to: adminEmails, // Send to all configured emails
        from: fromEmail,
        subject: `🚨 Error Alert: ${context.module} - ${errorMessage.substring(0, 50)}${errorMessage.length > 50 ? '...' : ''}`,
        body: emailBody,
      };

      await sendEmail(emailPayload, token);
      console.log(`[Error Handler] Error notification email sent to ${adminEmails.join(', ')}`);
    } catch (emailError) {
      console.error('[Error Handler] Failed to send error notification email:', emailError);
      // Don't throw - we don't want email failures to break the app
    }
  } else if (shouldSendEmail && !token) {
    console.warn('[Error Handler] Cannot send error email: No token provided');
  }

  // Re-throw error if requested
  if (shouldThrow) {
    throw error;
  }
}

// // Wrapper for API calls that automatically handles errors and sends emails
 
//   @example
// //  ```typescript
//   try {
//     const response = await fetch(url, options);
//     if (!response.ok) throw new Error(`API Error: ${response.status}`);
//    return await response.json();
//  } catch (error) {
//    await handleApiError(error, {
//      module: "inventory",
//      action: "fetch_products",
//      url: url,
//      method: "GET"
//    }, token);
//    throw error; // Re-throw if you want to handle it further
//  }

export async function handleApiError(
  error: any,
  context: ErrorContext,
  token?: string | null
): Promise<void> {
  await handleErrorWithEmail(error, context, token, {
    sendEmail: true,
    logToConsole: true,
    throwError: false
  });
}

/**
 * Create a fetch wrapper that automatically handles errors
 * 
 * @example
 * ```typescript
 * const response = await fetchWithErrorHandling(
 *   `${API_URL}/products`,
 *   {
 *     method: 'GET',
 *     headers: { Authorization: `Bearer ${token}` }
 *   },
 *   {
 *     module: "inventory",
 *     action: "fetch_products"
 *   },
 *   token
 * );
 * ```
 */
function getClientInfo() {
  if (typeof window === "undefined") return undefined;

  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
    },
  };
}

export async function fetchWithErrorHandling(
  url: string,
  options: RequestInit = {},
  context: Omit<ErrorContext, 'url' | 'method' | 'requestBody'>,
  token?: string | null
): Promise<Response> {
  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.clone().json();
        errorMessage = errorData?.error || errorData?.message || errorMessage;
      } catch {
        // If JSON parsing fails, use status text
      }
      
      const error = new Error(errorMessage);
      await handleApiError(error, {
        ...context,
        url,
        method: options.method || 'GET',
        requestBody: options.body ? (typeof options.body === 'string' ? JSON.parse(options.body) : options.body) : undefined
      }, token);
      
      throw error;
    }
    
    return response;
  } catch (error) {
    await handleApiError(error, {
      ...context,
      url,
      method: options.method || 'GET',
      requestBody: options.body ? (typeof options.body === 'string' ? JSON.parse(options.body) : options.body) : undefined
    }, token);
    throw error;
  }
}

